import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, User, Phone, Sparkles } from 'lucide-react';
interface BookingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  lang: 'en' | 'es';
}
const t = {
  en: {
    title: 'Book Your Transformation',
    subtitle: 'Your chair is waiting, queen.',
    name: 'Full Name',
    namePlaceholder: 'Your name',
    phone: 'Phone Number',
    phonePlaceholder: '(203) 555-0123',
    service: 'Service',
    servicePlaceholder: 'Select a service',
    date: 'Preferred Date',
    submit: 'Book Now',
    services: ['Hair Styling & Blowout', 'Hair Coloring', 'Braids & Protective Styles', 'Silk Press', 'Manicure & Pedicure', 'Lash Extensions', 'Facial Treatment', 'Full Glam Package', 'Bridal / Special Event', 'Other'],
    success: "You're booked! We'll confirm your appointment shortly."
  },
  es: {
    title: 'Reserva Tu Transformación',
    subtitle: 'Tu silla te espera, reina.',
    name: 'Nombre Completo',
    namePlaceholder: 'Tu nombre',
    phone: 'Número de Teléfono',
    phonePlaceholder: '(203) 555-0123',
    service: 'Servicio',
    servicePlaceholder: 'Selecciona un servicio',
    date: 'Fecha Preferida',
    submit: 'Reservar Ahora',
    services: ['Peinado & Secado', 'Coloración de Cabello', 'Trenzas & Estilos Protectores', 'Alisado con Seda', 'Manicura & Pedicura', 'Extensiones de Pestañas', 'Tratamiento Facial', 'Paquete Glam Completo', 'Novia / Evento Especial', 'Otro'],
    success: '¡Reservado! Confirmaremos tu cita pronto.'
  }
};
export function BookingPopup({
  isOpen,
  onClose,
  lang
}: BookingPopupProps) {
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [date, setDate] = useState('');
  const text = t[lang];
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const message = lang === 'es' ? `🌟 *Nueva Reserva - ISASHY Beauty Center*\n\n👤 Nombre: ${name}\n📞 Teléfono: ${phone}\n💇‍♀️ Servicio: ${service}\n📅 Fecha: ${date}` : `🌟 *New Booking - ISASHY Beauty Center*\n\n👤 Name: ${name}\n📞 Phone: ${phone}\n💇‍♀️ Service: ${service}\n📅 Date: ${date}`;
    const whatsappUrl = `https://wa.me/19172090303?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setName('');
      setPhone('');
      setService('');
      setDate('');
      onClose();
    }, 2500);
  }
  return <AnimatePresence data-id="element-18">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-19" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-20">
            <div className="bg-brand-dark border border-brand-gold/20 w-full max-w-md pointer-events-auto relative overflow-hidden" data-id="element-21">
              {/* Gold accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-brand-gold" data-id="element-22" />

              {/* Close button */}
              <button onClick={onClose} className="absolute top-4 right-4 text-brand-cream/50 hover:text-brand-cream transition-colors z-10" data-id="element-23">
                <X className="w-5 h-5" data-id="element-24" />
              </button>

              <div className="p-8 md:p-10" data-id="element-25">
                {!submitted ? <>
                    <div className="text-center mb-8" data-id="element-26">
                      <Sparkles className="w-6 h-6 text-brand-gold mx-auto mb-3" data-id="element-27" />
                      <h3 className="font-serif text-2xl md:text-3xl text-brand-cream mb-2" data-id="element-28">
                        {text.title}
                      </h3>
                      <p className="text-brand-cream/50 font-sans text-sm" data-id="element-29">
                        {text.subtitle}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5" data-id="element-30">
                      {/* Name */}
                      <div data-id="element-31">
                        <label className="text-brand-cream/70 text-xs uppercase tracking-wider font-sans mb-2 flex items-center gap-2" data-id="element-32">
                          <User className="w-3.5 h-3.5 text-brand-gold" data-id="element-33" />
                          {text.name}
                        </label>
                        <input type="text" required value={name} onChange={e => setName(e.target.value)} placeholder={text.namePlaceholder} className="w-full bg-white/5 border border-brand-cream/10 text-brand-cream px-4 py-3 font-sans text-sm placeholder:text-brand-cream/30 focus:outline-none focus:border-brand-gold/50 transition-colors" data-id="element-34" />
                      </div>

                      {/* Phone */}
                      <div data-id="element-35">
                        <label className="text-brand-cream/70 text-xs uppercase tracking-wider font-sans mb-2 flex items-center gap-2" data-id="element-36">
                          <Phone className="w-3.5 h-3.5 text-brand-gold" data-id="element-37" />
                          {text.phone}
                        </label>
                        <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder={text.phonePlaceholder} className="w-full bg-white/5 border border-brand-cream/10 text-brand-cream px-4 py-3 font-sans text-sm placeholder:text-brand-cream/30 focus:outline-none focus:border-brand-gold/50 transition-colors" data-id="element-38" />
                      </div>

                      {/* Service */}
                      <div data-id="element-39">
                        <label className="text-brand-cream/70 text-xs uppercase tracking-wider font-sans mb-2 flex items-center gap-2" data-id="element-40">
                          <Sparkles className="w-3.5 h-3.5 text-brand-gold" data-id="element-41" />
                          {text.service}
                        </label>
                        <select required value={service} onChange={e => setService(e.target.value)} className="w-full bg-white/5 border border-brand-cream/10 text-brand-cream px-4 py-3 font-sans text-sm focus:outline-none focus:border-brand-gold/50 transition-colors appearance-none" data-id="element-42">
                          <option value="" disabled className="bg-brand-dark" data-id="element-43">
                            {text.servicePlaceholder}
                          </option>
                          {text.services.map(s => <option key={s} value={s} className="bg-brand-dark" data-id="element-44">
                              {s}
                            </option>)}
                        </select>
                      </div>

                      {/* Date */}
                      <div data-id="element-45">
                        <label className="text-brand-cream/70 text-xs uppercase tracking-wider font-sans mb-2 flex items-center gap-2" data-id="element-46">
                          <Calendar className="w-3.5 h-3.5 text-brand-gold" data-id="element-47" />
                          {text.date}
                        </label>
                        <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full bg-white/5 border border-brand-cream/10 text-brand-cream px-4 py-3 font-sans text-sm focus:outline-none focus:border-brand-gold/50 transition-colors" data-id="element-48" />
                      </div>

                      <button type="submit" className="w-full bg-brand-gold hover:bg-brand-gold/90 text-brand-dark py-4 font-sans font-medium tracking-wide transition-all duration-300 mt-2" data-id="element-49">
                        {text.submit}
                      </button>
                    </form>
                  </> : <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-12" data-id="element-50">
                    <div className="w-16 h-16 rounded-full bg-brand-gold/20 flex items-center justify-center mx-auto mb-6" data-id="element-51">
                      <Sparkles className="w-8 h-8 text-brand-gold" data-id="element-52" />
                    </div>
                    <p className="font-serif text-2xl text-brand-cream mb-2" data-id="element-53">
                      {text.success}
                    </p>
                  </motion.div>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}